/**
 * The tech activity report (BUILD_PLAN.md section 13.3; ADR 0123): per tech,
 * their blocks in order, each with its day, times, client, sources,
 * duration, ticket and status, and the day view's stats, each day's added
 * up. The times are in the tech's own timezone. Durations and stats are as
 * the day view counts them: the API computes both the same way.
 *
 * "Download CSV" saves the same rows. A ticket is named by its number; only
 * its link to Autotask carries the ID. Under it, whether that ticket is still
 * open: closed is the goal, not logged (ADR 0185).
 */
import { useMutation, useQuery } from "@tanstack/react-query";
import { useId } from "react";

import type { TechActivity, TechActivityRow } from "../../api/types";
import { useSession } from "../../app/session-context";
import { HipaaBadge } from "../../components/ui/Badge";
import { Panel } from "../../components/ui/Panel";
import { SourceTile } from "../../components/ui/SourceTile";
import { formatTimeIn } from "../../lib/dates";
import { saveBlob } from "../../lib/download";
import { formatDuration } from "../../lib/durations";
import { possessive } from "../../lib/possessive";
import { type Lane, laneOf } from "../../lib/sources";
import { count, errorMessage, pillButton, retryOnce } from "./common";
import { useReportFilters } from "./filters";
import { DayLink, StatusBadge, TicketCell } from "./ReportCells";

function techName(tech: TechActivity): string {
  return tech.user.display_name ?? tech.user.email ?? "Unnamed user";
}

/** A row's sources as the day view's tiles: each lane once. */
export function Sources({ sources }: { sources: TechActivityRow["sources"] }) {
  const lanes: Lane[] = [];
  const others: string[] = [];
  for (const source of sources) {
    const lane = laneOf(source);
    if (lane === null) {
      others.push(source);
    } else if (!lanes.includes(lane)) {
      lanes.push(lane);
    }
  }
  return (
    <span className="flex flex-wrap items-center gap-[5px]">
      {lanes.map((lane) => (
        <SourceTile key={lane} lane={lane} />
      ))}
      {others.map((source) => (
        <span key={source} className="text-[13px] capitalize text-muted">
          {source}
        </span>
      ))}
    </span>
  );
}

function Row({ row, tech }: { row: TechActivityRow; tech: TechActivity }) {
  const zone = tech.user.timezone;
  return (
    <tr className="border-t border-line align-top">
      <td className="py-3 pr-3 whitespace-nowrap">
        <DayLink day={row.day} userId={tech.user.id} tech={techName(tech)} />
      </td>
      <td className="py-3 pr-3 font-mono text-xs whitespace-nowrap">
        {`${formatTimeIn(row.started_at, zone)}–${formatTimeIn(row.ended_at, zone)}`}
      </td>
      <td className="py-3 pr-3">
        <span className="flex flex-col gap-1">
          {row.client ? <span className="font-medium">{row.client.name}</span> : <span className="text-muted">No client</span>}
          {row.client?.is_hipaa ? <HipaaBadge /> : null}
        </span>
      </td>
      <td className="py-3 pr-3">
        <Sources sources={row.sources} />
      </td>
      <td className="py-3 pr-3 whitespace-nowrap">
        <span className="font-semibold tracking-[-0.02em]">{formatDuration(row.seconds)}</span>
        {row.duration_changed ? <span className="ml-1.5 text-xs text-muted">changed</span> : null}
      </td>
      <td className="py-3 pr-3 font-mono text-xs whitespace-nowrap">
        <TicketCell number={row.ticket_number} url={row.ticket_url} state={row.ticket_state} />
      </td>
      <td className="py-3">
        <StatusBadge status={row.status} label={row.status_label} />
      </td>
    </tr>
  );
}

function TechSection({ tech }: { tech: TechActivity }) {
  const headingId = useId();
  const name = techName(tech);
  const { stats } = tech;
  return (
    <section aria-labelledby={headingId} className="flex flex-col gap-2">
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <h3 id={headingId} className="text-base font-[650] tracking-[-0.015em]">
          {name}
        </h3>
        <p className="text-[13px] text-muted">
          {[
            count(tech.rows.length, "block", "blocks"),
            `Captured ${formatDuration(stats.captured_seconds)}`,
            `Logged ${formatDuration(stats.logged_seconds)}`,
            `Capture gap ${formatDuration(stats.gap_seconds)}`,
            `${String(stats.to_review)} to review`,
            `Times in ${tech.user.timezone}`,
          ].join(" · ")}
        </p>
      </div>
      {tech.rows.length === 0 ? (
        <p className="rounded-row bg-panel-2 px-4 py-3 text-muted">No blocks in these days.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-left text-[13.5px]">
            <caption className="sr-only">{`${possessive(name)} blocks`}</caption>
            <thead className="text-[12.5px] font-medium tracking-[0.08em] text-muted uppercase">
              <tr>
                <th scope="col" className="pb-2 pr-3">
                  Date
                </th>
                <th scope="col" className="pb-2 pr-3">
                  Time
                </th>
                <th scope="col" className="pb-2 pr-3">
                  Client
                </th>
                <th scope="col" className="pb-2 pr-3">
                  Sources
                </th>
                <th scope="col" className="pb-2 pr-3">
                  Duration
                </th>
                <th scope="col" className="pb-2 pr-3">
                  Ticket
                </th>
                <th scope="col" className="pb-2">
                  Status
                </th>
              </tr>
            </thead>
            <tbody>
              {tech.rows.map((row) => (
                <Row key={row.block_id} row={row} tech={tech} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

export function TechActivityReport() {
  const { api } = useSession();
  const headingId = useId();
  const [filters] = useReportFilters();
  const report = useQuery({
    queryKey: ["reports", "tech-activity", filters],
    queryFn: () => api.getTechActivityReport(filters),
    retry: retryOnce,
  });
  const download = useMutation({
    mutationFn: () => api.downloadTechActivityCsv(filters),
    onSuccess: (blob) => {
      saveBlob(blob, `tech-activity-${filters.start}-to-${filters.end}.csv`);
    },
  });

  return (
    <Panel aria-labelledby={headingId} className="flex flex-col gap-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex flex-col gap-1">
          <h2 id={headingId} className="text-lg font-semibold tracking-tight">
            Tech activity
          </h2>
          <p className="text-[13.5px] text-muted">
            Each tech&apos;s blocks in order, as their day view counts them.
          </p>
        </div>
        <button
          type="button"
          className={pillButton}
          disabled={!report.isSuccess || download.isPending}
          onClick={() => {
            download.mutate();
          }}
        >
          Download CSV
        </button>
      </div>
      {download.isError ? (
        <p role="alert" className="text-sm text-status-needs-client-fg">
          Not downloaded: {errorMessage(download.error)}
        </p>
      ) : null}
      {report.isPending ? (
        <p role="status" className="text-muted">
          Loading the report…
        </p>
      ) : report.isError ? (
        <p role="alert" className="text-status-needs-client-fg">
          {errorMessage(report.error)}
        </p>
      ) : report.data.techs.length === 0 ? (
        <p className="py-6 text-center text-muted">No blocks in these days.</p>
      ) : (
        report.data.techs.map((tech) => <TechSection key={tech.user.id} tech={tech} />)
      )}
    </Panel>
  );
}
